import React, { CSSProperties } from "react";
import classNames from "classnames";
import { ripple } from "../ripple/ripple";
import SvgIndeterminateCheckBox from "../icons/IndeterminateCheckBox";
import SvgCheckBoxOutlineBlank from "../icons/CheckBoxOutlineBlank";
import SvgRadioButtonUnchecked from "../icons/RadioButtonUnchecked";
import SvgCheckBox from "../icons/CheckBox";
import SvgRadioButtonChecked from "../icons/RadioButtonChecked";

interface ToggleProps {
    id?: string;
    name?: string;
    label?: string;
    labelClassName?: string;
    labelStyle?: CSSProperties | undefined;
    textClassName?: string;
    textStyle?: CSSProperties | undefined;
    edge?: "" | "start" | "end";
    radio?: boolean;
    value?: any;
    model?: any;
    checkedImage?: any;
    uncheckedImage?: any;
    disableRipple?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    className?: string;
    color?: "" | "default" | "inherit" | "primary" | "secondary";
    size?: "" | "sm" | "md" | "lg" | "xl";
    compact?: boolean;
    labelPlacement?: "start" | "end";
    indeterminate?: boolean;
    indeterminateImage?: any;
    inputRef?: any;
    defaultChecked?: any;

    onChange?: (e: any) => void;
    setModel?: (e: any) => void;
    onMouseDown?: (e: any) => void;
    onMouseLeave?: (e: any) => void;
    onFocus?: (e: any) => void;
    onBlur?: (e: any) => void;
    [x: string]: any;
}

export const Toggle = ({
    id,
    name,
    label,
    labelClassName = "",
    labelStyle,
    textClassName,
    textStyle,
    radio,
    value,
    model,
    checked,
    checkedImage,
    uncheckedImage,
    disableRipple,
    disabled,
    readOnly,
    className,
    color,
    size,
    edge,
    compact,
    labelPlacement,
    indeterminate,
    indeterminateImage,
    inputRef,
    defaultChecked,

    onChange,
    setModel,
    onMouseDown,
    onMouseLeave,
    onFocus,
    onBlur,
    ...props
}: ToggleProps) => {
    if (!labelPlacement) labelPlacement = "end";

    if (color === "inherit") color = "";
    else if (!color) color = "primary";

    var cn = classNames(className, "toggle", {
        "toggle-primary": color === "primary",
        "toggle-secondary": color === "secondary",
        "toggle-sm": size === "sm",
        "toggle-md": size === "md",
        "toggle-lg": size === "lg",
        "toggle-xl": size === "xl",
        "edge-start": edge === "start",
        "edge-end": edge === "end",
        "bd-compact": compact,
        disabled: !!disabled,
    });

    var rippleProps = {
        onMouseLeave: (e) => {
            if (!disableRipple) {
                ripple.removeFocus(e.currentTarget);
            }
            if (onMouseLeave) onMouseLeave(e);
        },
        onMouseDown: (e) => {
            if (!disableRipple) {
                ripple.pulse(e.currentTarget, 0, 0);
            }
            if (onMouseDown) onMouseDown(e);
        },
    };

    var inputProps: {};
    inputProps = {
        ref: inputRef,
        defaultChecked,
        type: radio ? "radio" : "checkbox",
        checked,
        indeterminate: indeterminate ? "true" : "false",
        onChange: (e) => {
            if (onChange) onChange(e);
            if (setModel) {
                if (radio) setModel(e.target.value);
                else setModel(e.target.checked);
            }
        },

        onFocus: (e) => {
            if (!disableRipple) {
                ripple.focus(e.currentTarget.parentNode);
            }
            if (onFocus) onFocus(e);
        },

        onBlur: (e) => {
            if (!disableRipple) {
                ripple.blur(e.currentTarget.parentNode);
            }
            if (onBlur) onBlur(e);
        },

        ...props,
    };

    if (disabled || readOnly) inputProps = { ...inputProps, disabled: true };
    if (model !== undefined) {
        var v = value === undefined ? true : value;
        inputProps = { ...inputProps, checked: model === v };
    }

    function renderText() {
        return (
            label && (
                <span className={classNames("toggle-text", textClassName)} style={textStyle}>
                    {label}
                </span>
            )
        );
    }

    function renderCheckbox() {
        var unchecked;
        if (indeterminate) unchecked = indeterminateImage ? indeterminateImage : <SvgIndeterminateCheckBox />;
        else if (uncheckedImage) unchecked = uncheckedImage;
        else unchecked = radio ? <SvgRadioButtonUnchecked /> : <SvgCheckBoxOutlineBlank />;

        return (
            <span className={cn} {...rippleProps}>
                <input id={id} name={name} value={value} {...inputProps} />
                <span>{unchecked}</span>
                <span>{!!checkedImage ? checkedImage : radio ? <SvgRadioButtonChecked /> : <SvgCheckBox />}</span>
            </span>
        );
    }

    return (
        <label
            className={classNames("toggle-label", labelClassName, { "text-secondary-text": disabled, "disable-ripple": disableRipple })}
            style={labelStyle}
        >
            {labelPlacement === "start" && renderText()}
            {renderCheckbox()}
            {labelPlacement === "end" && renderText()}
        </label>
    );
};
